import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Share2, Copy } from 'lucide-react';

export default function PublicProduct() {
  const { slug, productId } = useParams();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, [slug, productId]);
  
  useEffect(() => {
    if (product && store) {
      document.title = `${product.title} - ${store.name}`;
    }
  }, [product, store]);
  
  const fetchData = async () => {
    try {
      const [productRes, storeRes] = await Promise.all([
        fetch(`${API}/public/products/${productId}`),
        fetch(`${API}/public/stores/${slug}`),
      ]);
      
      if (!productRes.ok || !storeRes.ok) throw new Error('Not found');
      
      const productData = await productRes.json();
      const storeData = await storeRes.json();
      
      setProduct(productData);
      setStore(storeData);
      
      // Track product page view
      trackPageView(slug, productId);
    } catch (error) {
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };
  
  const trackPageView = async (storeSlug, prodId) => {
    try {
      await fetch(`${API}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'page_view',
          store_slug: storeSlug,
          product_id: prodId,
          domain: window.location.hostname
        })
      });
    } catch (e) {
      // Silent fail
    }
  };
  
  const trackAffiliateClick = async (storeSlug, prodId) => {
    try {
      await fetch(`${API}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'affiliate_click',
          store_slug: storeSlug,
          product_id: prodId,
          domain: window.location.hostname
        })
      });
    } catch (e) {
      // Silent fail
    }
  };
  
  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Product link copied!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };
  
  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!product || !store) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-outfit font-bold mb-2">Product Not Found</h1>
          <Link to={`/s/${slug}`} className="text-primary hover:underline">
            Back to Store
          </Link>
        </div>
      </div>
    );
  }
  
  const themeClasses = {
    minimalist: 'bg-white text-zinc-950',
    hypebeast: 'bg-zinc-950 text-white',
    curator: 'bg-amber-50 text-amber-950',
  };
  
  const themeClass = themeClasses[store.theme_id] || themeClasses.minimalist;
  
  return (
    <div className={`min-h-screen ${themeClass}`}>
      {/* Header */}
      <header className="border-b backdrop-blur-sm bg-opacity-80">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}`} data-testid="back-to-store-button">
              <Button variant="ghost" size="sm" className="rounded-full">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-outfit font-bold">{store.name}</h1>
            </div>
          </div>
        </div>
      </header>
      
      {/* Product Detail */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid md:grid-cols-2 gap-12"
        >
          {/* Images */}
          <div>
            {product.images && product.images.length > 0 ? (
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100">
                  <img
                    data-testid="product-main-image"
                    src={product.images[0]}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {product.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-4">
                    {product.images.slice(1, 5).map((img, i) => (
                      <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="aspect-square rounded-2xl bg-muted flex items-center justify-center">
                <p className="text-muted-foreground">No image available</p>
              </div>
            )}
          </div>
          
          {/* Details */}
          <div>
            <h1 data-testid="product-title" className="text-3xl md:text-4xl font-outfit font-bold mb-4">
              {product.title}
            </h1>
            
            {product.price && (
              <p data-testid="product-price" className="text-2xl font-bold mb-6">{product.price}</p>
            )}
            
            {product.recommendation && (
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 mb-6">
                <h3 className="font-outfit font-bold mb-2">Why I Recommend This</h3>
                <p data-testid="product-recommendation" className="leading-relaxed">{product.recommendation}</p>
              </div>
            )}
            
            {product.description && (
              <div className="mb-6">
                <h3 className="font-outfit font-bold mb-2">Description</h3>
                <p data-testid="product-description" className="opacity-80 leading-relaxed">{product.description}</p>
              </div>
            )}
            
            {/* Actions */}
            <div className="space-y-3">
              <a
                href={product.affiliate_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buy-now-button"
                onClick={() => trackAffiliateClick(slug, product.product_id)}
              >
                <Button className="w-full h-12 rounded-full font-bold text-lg">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Buy Now
                </Button>
              </a>
              
              <div className="flex gap-2">
                <Button
                  data-testid="share-product-button"
                  onClick={handleShare}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button
                  data-testid="copy-link-button"
                  onClick={() => handleCopyLink(product.affiliate_link)}
                  variant="outline"
                  className="flex-1 rounded-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}